import React, {useMemo} from 'react';
import {Typography, useTheme} from '@mui/material';
import {useLocalization} from 'localization';
import {
  RestrictedCodelistsContext,
  useRestrictedCodelistsContextProviderValue,
} from 'services/codelists';
import {
  useExpressionEditValue,
  useGetExpressionEditValue,
  useSetExpressionEditValue,
} from 'services/metadata';
import {Card, IconButton, Layout, Spacer, Text} from 'components';
import {DataForm} from 'schema/form';
import {ExpressionTitle} from 'scenes/updateMetadata/components/ExpressionCard/ExpressionTitle';
import {interceptExpressionSetFieldValue} from 'scenes/updateMetadata/components/ExpressionCard/functions';
import {useExpressionFormConfiguration} from 'scenes/updateMetadata/hooks';
import type {ExpressionCardProps} from './types';
import {ManifestationCard} from '../ManifestationCard';
import {ReadonlyExpression} from './ReadonlyExpression';
import {useExpressionEditState} from './hooks/useExpressionEditState';

export const ExpressionCard: React.FC<ExpressionCardProps> = props => {
  const {t, tLoose} = useLocalization();
  const {colors} = useTheme();

  const formConfiguration = useExpressionFormConfiguration();

  const getEditValue = useGetExpressionEditValue();
  const setEditValue = useSetExpressionEditValue(
    props.expressionId,
    'METADATAEDIT_EXPRESSION_EDITED',
  );

  const {
    codelistRestrictor,
    getLocalCodelist,
    relatedScope,
    schema,
    readonly,
    editStatus,
    valid,
    showErrors,
    handleEdit,
    handleCancelEdit,
  } = useExpressionEditState(props);

  const restrictedCodelistsContextValue =
    useRestrictedCodelistsContextProviderValue(
      codelistRestrictor,
      getLocalCodelist,
    );

  const ChangeExpressionText: React.ReactNode = useMemo(() => {
    const preTextSx = {
      lineHeight: '1.2rem',
    };

    return (
      <Typography
        variant={'h4'}
        color={props.readonly ? undefined : 'primary'}
        sx={preTextSx}>
        {t('form.expression.change.title')}
      </Typography>
    );
  }, [props.readonly, t]);

  return (
    <RestrictedCodelistsContext.Provider
      value={restrictedCodelistsContextValue}>
      <Card level="level2" headerSpacing={0}>
        <Card.Header>
          <Layout horizontal flex={1} alignItems="center">
            <Text kind="primary" variant="h4">
              <ExpressionTitle expressionId={props.expressionId} />
            </Text>
            <Layout horizontal adjustCenter adjustRight>
              {showErrors && (valid === 'warning' || valid === 'error') ? (
                <>
                  <Text variant="h4" kind={valid}>
                    {tLoose(`form.global.validation.${valid}.label`)}
                  </Text>
                  <Spacer width={4} />
                </>
              ) : null}
              {readonly ? (
                <IconButton
                  preText={ChangeExpressionText}
                  icon="DropDown"
                  disabled={props.readonly}
                  onClick={handleEdit}
                />
              ) : editStatus === 'Unchanged' ? (
                <IconButton
                  preText={ChangeExpressionText}
                  icon="ChevronUp"
                  onClick={handleCancelEdit}
                />
              ) : null}
            </Layout>
          </Layout>
        </Card.Header>
        <Card.Content>
          {readonly ? (
            <ReadonlyExpression
              expressionId={props.expressionId}
              schema={schema}
            />
          ) : (
            <Layout paddingTop={1}>
              <Card colorx={colors.brown}>
                <Card.Header minHeight={'20px'}>
                  <Text kind="primary" variant="h4" textTransform="capitalize">
                    {t('form.expression.header.title')}
                  </Text>
                </Card.Header>
                <Card.Content>
                  <DataForm
                    id={props.expressionId}
                    schema={schema}
                    configuration={formConfiguration}
                    relatedData={relatedScope}
                    mode={readonly ? 'read-only' : 'edit'}
                    showErrors={showErrors}
                    getEditValue={getEditValue}
                    useEditValue={useExpressionEditValue}
                    setEditValue={setEditValue}
                    setFieldValueModifier={interceptExpressionSetFieldValue}
                  />
                </Card.Content>
              </Card>
            </Layout>
          )}
          <Spacer size={2} />
          {props.content.map(mcr => (
            <ManifestationCard
              key={mcr.id}
              initialExpanded={mcr.id === props.initialManifestationId}
              manifestationId={mcr.id}
              expressionId={props.expressionId}
              changeRequests={mcr.changeRequests}
              workId={props.workId}
              readonly={props.readonly}
            />
          ))}
        </Card.Content>
      </Card>
    </RestrictedCodelistsContext.Provider>
  );
};
